import { PostgrestClient } from "@supabase/postgrest-js";
import type { DatabaseWithOptions, Json } from "../../__definitions__/database-definitions";
import { getClaims } from "../../server/supabase/hooks/get-claims";
import type { UUID } from "../../utils/uuid";

type MomentSubType =
  | "recipe_text"
  | "discover_customise"
  | "discover_refine_initial"
  | "discover_refine"
  | "recipe_refine"
  | "copilot"
  | "copilot_refine"
  | "copilot_refine_section"
  | "tone_summary";

type ContentGenerationParams =
  | { contentType: "moment"; subType?: MomentSubType; prompt: string }
  | { contentType: "mfai"; subType?: string; prompt: string }
  | { contentType: "survey"; subType?: string | null; prompt: string };

type SupabaseClient = PostgrestClient<DatabaseWithOptions["db"], DatabaseWithOptions["options"]>;

function createSupabaseClient(token: string): SupabaseClient {
  const claims = getClaims(token);
  const url = claims["http://changeengine.com/supabase_url"];
  const key = claims["http://changeengine.com/supabase_anon_key"];

  return new PostgrestClient(`${url}/rest/v1`, {
    headers: {
      apikey: key,
      Authorization: `Bearer ${token}`,
    },
    schema: "public",
  });
}

async function resolveProfileId(supabase: SupabaseClient, authSub: string): Promise<UUID | null> {
  const { data } = await supabase.from("profile").select("id").eq("auth0_id", authSub).single();

  return data?.id ?? null;
}

export async function trackContentGeneration(
  token: string,
  params: ContentGenerationParams,
): Promise<UUID | null> {
  try {
    const claims = getClaims(token);
    const supabase = createSupabaseClient(token);

    const profileId = await resolveProfileId(supabase, claims.sub);
    if (!profileId) return null;

    const { data } = await supabase
      .from("content_generation")
      .insert({
        profile_id: profileId,
        content_type: params.contentType,
        sub_type: params.subType ?? null,
        prompt: params.prompt,
      })
      .select("id")
      .single();

    return data?.id ?? null;
  } catch (error) {
    console.error("Failed to track content generation:", error);
    return null;
  }
}

type TrackedResult<T> = {
  data: T;
  contentGenerationId: UUID | null;
  contentGenerationResultId: UUID | null;
};

/**
 * Run an AI request in parallel with content generation tracking.
 * After both settle, inserts a `content_generation_result` row with the
 * AI response snapshot. Returns both the AI data and the tracking ID.
 *
 * The AI request is never blocked by tracking — if tracking fails the
 * AI response is still returned with `contentGenerationId: null`.
 */
export async function withContentTracking<T extends Json>(
  token: string,
  params: ContentGenerationParams,
  request: () => Promise<T>,
): Promise<TrackedResult<T>> {
  const [trackingResult, data] = await Promise.allSettled([
    trackContentGeneration(token, params),
    request(),
  ]);

  if (data.status === "rejected") throw data.reason;

  const contentGenerationId = trackingResult.status === "fulfilled" ? trackingResult.value : null;

  let contentGenerationResultId: UUID | null = null;
  if (contentGenerationId) {
    contentGenerationResultId = await trackContentGenerationResult(
      token,
      contentGenerationId,
      data.value,
    );
  }

  return { data: data.value, contentGenerationId, contentGenerationResultId };
}

export async function trackContentGenerationResult(
  token: string,
  contentGenerationId: UUID,
  content: Json,
): Promise<UUID | null> {
  try {
    const supabase = createSupabaseClient(token);

    const { data } = await supabase
      .from("content_generation_result")
      .insert({
        content_generation_id: contentGenerationId,
        content,
      })
      .select("id")
      .single();

    return data?.id ?? null;
  } catch (error) {
    console.error("Failed to track content generation result:", error);
    return null;
  }
}

export async function linkDigitalAssetToContentGenerationResult(
  token: string,
  contentGenerationResultId: UUID,
  digitalAssetId: UUID,
): Promise<void> {
  try {
    const supabase = createSupabaseClient(token);

    await supabase
      .from("content_generation_result")
      .update({ digital_asset_id: digitalAssetId, saved_to_content_library: true })
      .eq("id", contentGenerationResultId);
  } catch (error) {
    console.error("Failed to link digital asset to content generation result:", error);
  }
}

export async function markContentGenerationResultExported(
  token: string,
  contentGenerationResultId: UUID,
): Promise<void> {
  try {
    const supabase = createSupabaseClient(token);

    await supabase
      .from("content_generation_result")
      .update({ exported: true })
      .eq("id", contentGenerationResultId);
  } catch (error) {
    console.error("Failed to mark content generation result as exported:", error);
  }
}

export async function linkMomentToContentGenerationResult(
  token: string,
  contentGenerationResultId: UUID,
  momentId: UUID,
): Promise<void> {
  try {
    const supabase = createSupabaseClient(token);

    await supabase
      .from("content_generation_result")
      .update({ moment_id: momentId })
      .eq("id", contentGenerationResultId);
  } catch (error) {
    console.error("Failed to link moment to content generation result:", error);
  }
}
